import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  DatePicker,
  Table,
  Alert,
  ConfigProvider,
  Select,
  InputNumber,
  message,
  Input,
} from 'antd';
import { EmptyFileColor } from 'patternx-svg';
import type { DatePickerProps } from 'antd';
import moment from 'moment';
import 'moment/locale/zh-cn';
import PtxEmpty from '@/components/ptx-empty';
import { getFpsEnum, getFpsByPages, downoadAllApi, getFpsStatistics } from '@/services/initial';
import { isNaN } from 'lodash-es';
import './index.less';

moment.locale('zh-cn');

type searchParams = {
  dateFrom: number;
  dateTo: number;
  timeSpan: number;
  bizType: string[];
  clientType: string[];
  fileType: string[];
  resolution: string[];
  fileName: string;
  statisticalScope: string;
};

const defalutTo = moment();
const defaultFrom = moment().subtract(1, 'day');
const Fps: React.FC<{}> = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [param, setParam] = useState<searchParams>({
    dateFrom: defaultFrom.valueOf(),
    dateTo: defalutTo.valueOf(),
  } as searchParams);
  const [data, setData] = useState<any>();
  const [bizTypes, setBizTypes] = useState<any[]>([]);
  const [clientType, setClientType] = useState<any[]>([]);
  const [resolution, setResolution] = useState<any[]>([]);
  const [fileNameValue, setFileNameValue] = useState<any>(undefined);
  const [statisticalScope, setStatisticalScope] = useState<any>(undefined);
  const [page, setPage] = useState<number>(1); // 当前页码
  const [size, setSize] = useState<number>(10); // 每页显示数据数量
  const [total, setTotal] = useState<number>(0); // 总共数据量
  const [fileInfo, setFileInfo] = useState({
    max: 0,
    mean: 0,
    min: 0,
    totalRecord: 0,
    scopeCnt: 0,
    scopeRatio: '',
  });

  const columns: any = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      render: (text: any, record: any, index: number) => (
        <div style={{ width: 50 }}>{index + 1}</div>
      ),
    },
    {
      title: '业务线',
      dataIndex: 'bizType',
      key: 'bizType',
      render: (text: any) => <div style={{ width: 160 }}>{text}</div>,
    },
    {
      title: '分组Id',
      dataIndex: 'ueInstanceId',
      key: 'ueInstanceId',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: '时间',
      dataIndex: 'time',
      key: 'time',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: 'FPS',
      dataIndex: 'fps',
      key: 'fps',
      render: (text: any) => <div style={{ width: 140 }}>{Math.round(text || 0)}</div>,
    },
    {
      title: '分辨率',
      dataIndex: 'resolution',
      key: 'resolution',
      render: (text: any) => <div style={{ width: 60 }}>{text || '-'}</div>,
    },
    {
      title: '文件名',
      dataIndex: 'fileId',
      key: 'fileId',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (text: any) => <div style={{ width: 60 }}>{text || '-'}</div>,
    },
    {
      title: '用户',
      dataIndex: 'userName',
      key: 'userName',
      render: (text: any) => <div style={{ width: 60 }}>{text || '-'}</div>,
    },
    {
      title: '客户端类型',
      dataIndex: 'clientType',
      key: 'clientType',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: '企业名称',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
    {
      title: '业务侧标识',
      dataIndex: 'bizId',
      key: 'bizId',
      render: (text: any) => <div style={{ width: 160 }}>{text || '-'}</div>,
    },
  ];

  const renderEmpty = () => {
    return <PtxEmpty isEmpty={true} describe="暂无数据" icon={<EmptyFileColor />} />;
  };

  const returnParam = (params?: { value1?: number; value2?: number }) => {
    if (isNaN(params?.value1)) {
      return undefined;
    }
    if (!params?.value1) {
      return params?.value2 || undefined;
    }
    return params?.value1;
  };

  const getEnums = async (type: string) => {
    const res = await getFpsEnum({
      type,
    });
    if (res?.success) {
      const data = res?.data?.details || [];
      if (type === 'bizType') {
        setBizTypes(
          data.map((d: string) => {
            return {
              label: d,
              value: d,
            };
          }),
        );
      }
      if (type === 'clientType') {
        setClientType(
          data.map((d: string) => {
            return {
              label: d,
              value: d,
            };
          }),
        );
      }
      if (type === 'resolution') {
        setResolution(
          data.map((d: string) => {
            return {
              label: d,
              value: d,
            };
          }),
        );
      }
    }
  };

  const getDatas = async (params?: {
    dateFrom?: number;
    dateTo?: number;
    bizType?: string[];
    clientType?: string[];
    fileType?: string[];
    resolution?: string[];
    timeSpan?: number;
    showTips?: boolean;
    fileName?: string;
    statisticalScope?: string;
  }) => {
    const dateFrom = returnParam({ value1: params?.dateFrom, value2: param?.dateFrom });
    const dateTo = returnParam({ value1: params?.dateTo, value2: param?.dateTo });
    if (!dateFrom || !dateTo) {
      if (params?.showTips) {
        message.error('请选择时间范围');
      }
      return;
    }
    setIsLoading(true);
    const res = await getFpsByPages({
      dateFrom: dateFrom,
      dateTo: dateTo,
      bizType: params?.bizType || param?.bizType,
      clientType: params?.clientType || param?.clientType,
      fileType: params?.fileType || param?.fileType,
      resolution: params?.resolution || param?.resolution,
      timeSpan: params?.timeSpan || param?.timeSpan,
      fileName: params?.fileName === '' ? '' : params?.fileName || param?.fileName,
      statisticalScope:
        params?.statisticalScope === '' ? '' : params?.statisticalScope || param?.statisticalScope,
      pageIndex: page,
      pageSize: size,
    });
    setIsLoading(false);
    if (res?.success) {
      if (res.data.current > res.data.pages) {
        const resOnceMore = await await getFpsByPages({
          dateFrom: dateFrom,
          dateTo: dateTo,
          bizType: params?.bizType || param?.bizType,
          clientType: params?.clientType || param?.clientType,
          fileType: params?.fileType || param?.fileType,
          resolution: params?.resolution || param?.resolution,
          timeSpan: params?.timeSpan || param?.timeSpan,
          fileName: params?.fileName === '' ? '' : params?.fileName || param?.fileName,
          statisticalScope:
            params?.statisticalScope === ''
              ? ''
              : params?.statisticalScope || param?.statisticalScope,
          pageIndex: res.data.pages || 1,
          pageSize: size,
        });
        const datas = resOnceMore?.data;
        setData(datas);
        setTotal(datas.total || 0);
        setPage(datas.current || 1);
        setSize(datas.size || 10);
        // fileNameHanlde(resOnceMore)
        console.log('getFpsDatasOnceMore', res, resOnceMore);
      } else {
        console.log('getFpsDatas', res);
        const datas = res?.data;
        setData(datas);
        setTotal(datas?.total || 0);
        setPage(datas?.current || 1);
        setSize(datas?.size || 10);
        // fileNameHanlde(res)
      }
    }
  };

  const getStatistics = async (params?: {
    dateFrom?: number;
    dateTo?: number;
    bizType?: string[];
    clientType?: string[];
    fileType?: string[];
    resolution?: string[];
    timeSpan?: number;
    showTips?: boolean;
    fileName?: string;
    statisticalScope?: string;
  }) => {
    // 获取fps的统计数据
    const dateFrom = returnParam({ value1: params?.dateFrom, value2: param?.dateFrom });
    const dateTo = returnParam({ value1: params?.dateTo, value2: param?.dateTo });
    const res = await getFpsStatistics({
      dateFrom: dateFrom,
      dateTo: dateTo,
      bizType: params?.bizType || param?.bizType,
      clientType: params?.clientType || param?.clientType,
      fileType: params?.fileType || param?.fileType,
      resolution: params?.resolution || param?.resolution,
      timeSpan: params?.timeSpan || param?.timeSpan,
      fileName: params?.fileName === '' ? '' : params?.fileName || param?.fileName,
      statisticalScope:
        params?.statisticalScope === '' ? '' : params?.statisticalScope || param?.statisticalScope,
    });
    console.log('getFpsStatistics', res, res?.data.details);
    if (res.success) {
      setFileInfo(res?.data.details);
    }
  };

  const init = () => {
    getEnums('bizType'); // 获取业务线选项列表
    getEnums('clientType'); // 获取终端选项列表
    getEnums('resolution'); // 获取分辨率选项列表
    getStatistics(); // 获取fps的统计数据
    getDatas();
  };

  // 搜索-时间范围-起始时间
  const onChange1 = (value: DatePickerProps['value']) => {
    const dateFrom = value?.valueOf() || 0;
    setParam({
      ...param,
      dateFrom,
    });
    getDatas({
      dateFrom,
    });
    getStatistics({
      dateFrom,
    });
  };

  // 搜索-时间范围-截止时间
  const onChange2 = (value: DatePickerProps['value']) => {
    const dateTo = value?.valueOf() || 0;
    setParam({
      ...param,
      dateTo,
    });
    getDatas({
      dateTo,
    });
    getStatistics({
      dateTo,
    });
  };

  // 搜索-业务线
  const handleChange1 = (value: string[]) => {
    setParam({
      ...param,
      bizType: value,
    });
    getDatas({
      bizType: value,
      showTips: true,
    });
    getStatistics({
      bizType: value,
    });
  };

  // 搜索-有效时间
  const onblur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.round(Number(e.target.value || 0));
    const timeSpan = value < 0 ? 0 : value > 5 ? 5 : value;
    setParam({
      ...param,
      timeSpan,
    });
    getDatas({
      timeSpan,
      showTips: true,
    });
    getStatistics({
      timeSpan,
    });
  };

  // 搜索-终端类型
  const handleChange2 = (value: string[]) => {
    setParam({
      ...param,
      clientType: value,
    });
    getDatas({
      clientType: value,
      showTips: true,
    });
    getStatistics({
      clientType: value,
    });
  };

  // 搜索-文件类型
  const handleChange3 = (value: string[]) => {
    setParam({
      ...param,
      fileType: value,
    });
    getDatas({
      fileType: value,
      showTips: true,
    });
    getStatistics({
      fileType: value,
    });
  };

  // 搜索-分辨率
  const handleChange4 = (value: string[]) => {
    setParam({
      ...param,
      resolution: value,
    });
    getDatas({
      resolution: value,
      showTips: true,
    });
    getStatistics({
      resolution: value,
    });
  };

  // 搜索-文件名称
  const handleFileNameChange = (e: any) => {
    const value = e.target.value;
    console.log('handleFileNameChange', value);
    if (value) {
      setFileNameValue(value);
    } else {
      setFileNameValue(undefined);
      getDatas({
        fileName: '',
      });
      getStatistics({
        fileName: '',
      });
    }
    setParam({
      ...param,
      fileName: value,
    });
  };

  //指标范围
  const handleStatisticalScopeChange = (e: any) => {
    const value = e.target.value;
    if (value) {
      setStatisticalScope(value);
    } else {
      setStatisticalScope(undefined);
      getDatas({
        // 搜索框内容全部删除时，重置table数据
        statisticalScope: '',
      });
      getStatistics({
        statisticalScope: '',
      });
    }
    setParam({
      ...param,
      statisticalScope: value,
    });
  };

  // 搜索-确认搜索
  const onSearch = () => {
    getDatas({
      showTips: true,
    });
    getStatistics();
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getDatas();
  }, [page, size]);

  const downoadAll = async () => {
    message.info('请稍等，正在导出文件');
    const res = await downoadAllApi(param);
    if (res?.success) {
      const a = document.createElement('a');
      a.href = res?.data?.details;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="sla-list-content">
      <div className="sla-list-search">
        <Space>
          <span>时间范围：</span>
          <DatePicker onChange={onChange1} showTime defaultValue={defaultFrom} />
          <span>-</span>
          <DatePicker showTime onChange={onChange2} defaultValue={defalutTo} />
          <span>业务线：</span>
          <Select
            mode="multiple"
            style={{ width: 160 }}
            placeholder="请选择业务线"
            onChange={handleChange1}
            showArrow
            options={bizTypes}
            showSearch={false}
          />
          <span>有效时间：</span>
          <span>
            前{' '}
            <InputNumber
              // value={pitch}
              min={1}
              max={5}
              step={1}
              // onChange={changePitch}
              // onKeyDown={keyDownfn1}
              keyboard={false}
              precision={0}
              style={{ width: 60 }}
              onBlur={(e) => onblur(e)}
            />
            分钟
          </span>
          <span>统计指标范围：</span>
          <Input
            style={{ minWidth: 150 }}
            placeholder="请输入统计指标范围"
            allowClear
            value={statisticalScope}
            onChange={handleStatisticalScopeChange}
          />
        </Space>
        <Space style={{ marginTop: '12px' }}>
          <span>终端类型：</span>
          <Select
            mode="multiple"
            style={{ width: 160 }}
            placeholder="请选择终端类型"
            onChange={handleChange2}
            showArrow
            options={clientType}
            showSearch={false}
          />
          <span>文件类型：</span>
          <Select
            mode="multiple"
            style={{ width: 160 }}
            placeholder="请选择文件类型"
            onChange={handleChange3}
            showArrow
            showSearch={false}
            options={[
              {
                value: 'dwg',
                label: 'dwg',
              },
              {
                value: 'rvt',
                label: 'rvt',
              },
              {
                value: 'skp',
                label: 'skp',
              },
              {
                value: 'other',
                label: '其他',
              },
            ]}
          />
          <span>分辨率：</span>
          <Select
            mode="multiple"
            style={{ width: 160 }}
            placeholder="请选择分辨率"
            onChange={handleChange4}
            showArrow
            options={resolution}
            showSearch={false}
          />
          <span>文件名称：</span>
          <Input
            style={{ width: 160 }}
            placeholder="请输入文件名称"
            onChange={handleFileNameChange}
            allowClear
            value={fileNameValue}
          />
          <Button type="primary" ghost onClick={onSearch}>
            查询
          </Button>
          <Button onClick={downoadAll}>下载</Button>
        </Space>
        <Alert
          message={
            <>
              <div>
                FPS统计，平均：{fileInfo?.mean || 0}，最低：{fileInfo?.min || 0}，最高：
                {fileInfo?.max || 0}
              </div>
              {fileInfo?.scopeCnt ? (
                <div>
                  FPS统计，总数：{fileInfo?.totalRecord || 0}，范围数量：{fileInfo?.scopeCnt || 0}
                  ，范围占比：
                  {fileInfo?.scopeRatio || ''}
                </div>
              ) : null}
            </>
          }
          type="info"
          style={{ padding: '4px 15px', marginTop: '12px' }}
        />
      </div>

      <div className="sla-list-table">
        <ConfigProvider renderEmpty={renderEmpty}>
          <Table
            rowKey={(record) => record.id}
            loading={isLoading}
            columns={columns}
            dataSource={data?.records || []}
            scroll={{ x: true, y: 'calc(100vh - 345px)' }}
            pagination={{
              position: ['bottomRight'],
              size: 'small',
              showTotal: (total, range) => {
                return `${range[0]}-${range[1]}条，共${total}条`;
              },
              showTitle: true,
              showQuickJumper: false,
              showSizeChanger: true,
              total: total, // 分页总数
              pageSize: size, // 每页数量
              current: page, // 当前页数
              onChange: (p, s) => {
                console.log('翻页', { p, s });
                setPage(p);
                setSize(s as number);
              },
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default Fps;
